import qs from 'query-string';
import { isDemoChannel } from 'services/channels/demo';
import { doorClient, ImResponse, messDoorClient, resolve, storeHomeV2Client } from '.';

type Account = Assistant.Account;

export interface ListParams {
  page: number;
  pageSize: number;
  departmentId?: number;
  departmentIds?: number[];
  sleepFlag?: boolean;
  keyword?: string;
}

export interface ListResponse {
  data?: Account[];
  page: number;
  pageSize: number;
  total: number;
}

export type ListAssistantAccountsParams = ListParams & { includeSupervisor?: boolean };

export function list(params: ListAssistantAccountsParams, signal?: AbortSignal) {
  return resolve(
    doorClient.get<ImResponse<ListResponse>>('/overseaim-store-home/v1/assistant/page-list', {
      params,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'none' }),
      signal,
    }),
  );
}

export interface ListResponseV2 {
  data?: StoreHomeV2.Account[];
  page: number;
  pageSize: number;
  total: number;
}

/**
 * v2版本page-list
 * @param params
 * @returns
 */
export function listV2(params: ListParams) {
  return resolve(
    storeHomeV2Client.get<ImResponse<ListResponseV2>>('/admin/users/page-list', {
      params,
    }),
  );
}

export function create(body: {
  user: Pick<Account, 'account' | 'name' | 'password' | 'phoneNumber' | 'email' | 'type'>;
  newRoleIds: number[];
}) {
  return resolve(
    doorClient.post<ImResponse<undefined>>('/overseaim-store-home/v1/assistant', body),
  );
}

export function updateStatus(body: Pick<Account, 'id' | 'status'>) {
  return resolve(
    doorClient.put<ImResponse<undefined>>('/overseaim-store-home/v1/assistant/status', body),
  );
}

export function updateNick(body: Pick<Account, 'id' | 'name'>) {
  return resolve(
    doorClient.put<ImResponse<undefined>>('/overseaim-store-home/v1/assistant/name', body),
  );
}

/**
 * 更新帐号
 * @param body
 * @returns
 */
export function update(
  body: Pick<
    Account,
    'id' | 'name' | 'phoneNumber' | 'email' | 'type' | 'timeZone' | 'incomingLineLimit'
  > & {
    newRoleIds: number[];
  },
) {
  return resolve(
    messDoorClient.put<ImResponse<undefined>>('/overseaim-store-home/v1/assistant/basic', body),
  );
}

export interface BatchUpdateUserBody
  extends Partial<Pick<Account, 'timeZone' | 'incomingLineLimit'>> {
  userIds: number[];
}

export function batchUpdateUser(body: BatchUpdateUserBody) {
  return storeHomeV2Client.post('/admin/batch_update_user', body);
}

export interface UpdatePasswordBody {
  id: Account['id'];
  oldPasswd?: string;
  newPasswd: string;
}

export function updatePassword(body: UpdatePasswordBody) {
  if (isDemoChannel) {
    return Promise.reject(new Error('不允许修改'));
  }
  return resolve(
    messDoorClient.put<ImResponse<undefined>>('/overseaim-store-home/v1/assistant/passwd', body),
  );
}

export function del(id: Account['id']) {
  return resolve(
    doorClient.delete<ImResponse<undefined>>(`/overseaim-store-home/v1/assistant?id=${id}`),
  );
}

export interface OnlineStatus {
  status: Conversation.Status;
  subStatus?: string;
}

export function onlineStatus() {
  return doorClient.get<ImResponse<Conversation.Status, OnlineStatus>>(
    '/oversea-conversation/v1/assistant/online-status',
  );
}

export function seat() {
  return resolve(
    storeHomeV2Client.get<ImResponse<Assistant.SeatInfo>>('/admin/account/seat_count'),
  );
}

export interface Department {
  id: number;
  name: string;
  roles: StoreHomeV2.RoleWithDepartment[];
}

export interface AssignConversationConfig {
  assignConversation: boolean;
  buyerFilters: string[];
  assignBuyerAndOrderRule: {
    isAssignAfterSalesOrder: boolean;
    isAssignNegativeReview: boolean;
  };
  platformStoreScopes: General.StoreScope[];
  receptionScope: string;
  substituteRoleId: number;
  substituteType: string;
  tradeStatusFilters: string[];
}

export interface UserRolesResponse {
  userId: number;
  roleIds: number[];
}

export interface UserRolesRequest {
  userId: number;
  roleIds: number[];
}

/**
 * 设置角色
 */
export function configureUserRoles(data: UserRolesRequest) {
  return resolve(storeHomeV2Client.put<ImResponse<UserRolesResponse>>('/admin/user_roles', data));
}

export interface ApifoxModal {
  assignConversationConfig: AssignConversationConfig;
  description: string;
  name: string;
  permissionWithScopes: PermissionWithScope[];
  roleId: number;
}

export interface PermissionWithScope {
  permissionGroupName?: string;
  permissionId?: number;
  permissionName?: string;
  storeScope?: General.StoreScope['regionStoreScopes'][];
  platformStoreScopes?: PermissionWithScopePlatformStoreScope[];
}

export interface PermissionWithScopePlatformStoreScope {
  platform?: string;
  regionStoreScopes?: FluffyRegionStoreScope[];
  storeCount?: number;
}

export interface FluffyRegionStoreScope {
  region?: string;
  storeCount?: number;
  storeIds?: number[];
}

/**
 * 获取角色详情
 * @param roleId 角色id
 * @returns
 */
export const getRoleDetail = (roleId: number) => {
  return resolve(
    storeHomeV2Client.get<ImResponse<StoreHomeV2.RoleWithDepartment>>(`/admin/roles/${roleId}`),
  );
};

export interface CheckRolesConflictRequest {
  allRoleIds: number[];
}

export interface CheckRoleConflictResponse {
  baseRole: BaseRole;
  conflictRoles: ConflictRole[];
  isConflict: boolean;
}

export interface BaseRole {
  departmentId: number;
  departmentName: string;
  roleId: number;
  roleName: string;
}

export interface ConflictRole {
  departmentId?: number;
  departmentName?: string;
  roleId?: number;
  roleName?: string;
}

/**
 * 检查权限冲突
 * @param body
 * @returns
 */
export const checkRolesConflict = (body: CheckRolesConflictRequest) => {
  return resolve(
    storeHomeV2Client.post<ImResponse<CheckRoleConflictResponse>>(
      `/admin/roles_check_conflict`,
      body,
    ),
  );
};

interface QueryAssistantParams {
  user_ids: string[];
}

interface QueryAssistantData {
  id: string;
  account: string;
  orgId: number;
  name: string;
  status: string;
  password: string;
  accid: string;
  phoneNumber: string;
  email: string;
  sleepFlag: boolean;
  botEnabled: boolean;
  type: number;
  nonReceptionProcessDailyLimit: number;
  isDeleted: boolean;
  timeZone: number;
  lastTimeZoneUpdateTimeStamp: string;
  bindLogins: BindLogins;
  incomingLineLimit: number;
}

export type BindLogins = {
  isBind: boolean;
  name: string;
  type: 'GOOGLE' | 'FACEBOOK';
}[];

// 查询帐号信息
export function queryAssistant(params: QueryAssistantParams) {
  return resolve(
    doorClient.get<ImResponse<QueryAssistantData[]>>(
      '/overseaim-store-home/v1/assistant/query-assistant',
      {
        params: {
          ...params,
          user_ids: params.user_ids.join(','),
        },
      },
    ),
  );
}
